import { useRef, useCallback } from 'react';
import type { Highlight, PolygonHighlight, RectangleHighlight, PositionedHighlight } from '../types';

const CLOSE_DELAY = 2000;

interface UsePopupManagerOptions {
    setActivePopup: React.Dispatch<React.SetStateAction<any>>;
    overlayRef: React.RefObject<HTMLDivElement | null>;
    dimension: { width: number; height: number };
}

export function usePopupManager({ setActivePopup, overlayRef, dimension }: UsePopupManagerOptions) {
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const cancelClose = useCallback(() => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    }, []);

    const scheduleClose = useCallback(() => {
        cancelClose();
        closeTimer.current = setTimeout(() => {
            setActivePopup((prev: any) => (prev?.pinned ? prev : null));
        }, CLOSE_DELAY);
    }, [cancelClose, setActivePopup]);

    const openPopupForText = useCallback(
        (h: Highlight, el: HTMLElement) => {
            const overlay = overlayRef.current;
            if (!overlay) return;
            const rect = el.getBoundingClientRect();
            const base = overlay.getBoundingClientRect();
            cancelClose();
            setActivePopup({
                id: h.id,
                x: rect.left - base.left + rect.width / 2,
                y: rect.top - base.top,
                yBottom: rect.bottom - base.top,
                pinned: false
            });
        },
        [cancelClose, overlayRef, setActivePopup]
    );

    const openPopupForPolygon = useCallback((h: Highlight) => {
        const ph = h as PolygonHighlight;
        if (!ph.points.length) return;
        const cx = ph.points.reduce((s, p) => s + p.x, 0) / ph.points.length;
        const cy = ph.points.reduce((s, p) => s + p.y, 0) / ph.points.length;
        const px = cx * dimension.width;
        const py = cy * dimension.height;
        cancelClose();
        setActivePopup({ id: h.id, x: px, y: py - 9, yBottom: py + 9, pinned: false });
    }, [dimension, cancelClose, setActivePopup]);

    const openPopupForRect = useCallback((h: Highlight) => {
        const rh = h as RectangleHighlight;
        const cx = ((rh.x1 + rh.x2) / 2) * dimension.width;
        const cy = ((rh.y1 + rh.y2) / 2) * dimension.height;
        cancelClose();
        setActivePopup({ id: h.id, x: cx, y: cy - 9, yBottom: cy + 9, pinned: false });
    }, [dimension, cancelClose, setActivePopup]);

    const handleMarkerPopupEnter = useCallback((h: Highlight) => {
        cancelClose();
        const ph = h as PositionedHighlight;
        setActivePopup({
            id: h.id,
            x: ph.x * dimension.width,
            y: ph.y * dimension.height - 9,
            yBottom: ph.y * dimension.height + 9,
            pinned: false
        });
    }, [cancelClose, dimension, setActivePopup]);

    return {
        cancelClose,
        scheduleClose,
        openPopupForText,
        openPopupForPolygon,
        openPopupForRect,
        handleMarkerPopupEnter,
    };
}
